import { parsePhoneNumber } from 'libphonenumber-js';
import type { FieldValues } from 'react-hook-form';
import validator from 'validator';
import { defaultErrorMessages } from '../config/defaultContent';
import type {
  AnswerSumNilReturnOverride,
  AnswerSumReference,
  ErrorMessages,
  ListTotalConfig,
  Question,
  Rules,
  RulesInContent,
} from '../config/types';
import { defaultReservedCharacters } from '../constants';
import { calculateSelectedReportingPeriodHint } from './reporting-period';

export const getErrorMessage = (
  name: RulesInContent,
  label: string,
  value: string,
  errorMessage?: string,
) =>
  String(errorMessage || defaultErrorMessages[name] || '')
    .replace('$LABEL', label)
    .replace('$CONDITION', value);

export const isPostcode = (value: string) => {
  const re = /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i;
  return re.test(value);
};

export const isValidPassword = (value: string) => {
  const re =
    /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&+=)(\-_*])([a-zA-Z0-9!@#$%^&+=*)(\-_]){8,}$/;
  return re.test(value);
};

export const validateMobile = (value: string) => {
  const defaultCountryCode = 'GB';

  if (!value.startsWith('+') && !value.startsWith('0')) {
    return false;
  }

  try {
    const phoneNumber = parsePhoneNumber(value, defaultCountryCode);
    return phoneNumber?.isValid();
  } catch (_) {
    return false;
  }
};

export const isMinLength = (value: string | number, minLength: number) =>
  String(value).length >= minLength;

export const isMaxLength = (value: string | number, maxLength: number) =>
  String(value).length <= maxLength;

export const isMinNumber = (value: string, minNumber: number) => {
  const normalized = value.replace(/,/g, '');
  return validator.isNumeric(normalized) && Number(normalized) >= minNumber;
};

export const isMaxNumber = (value: string, maxNumber: number) => {
  const normalized = value.replace(/,/g, '');
  return validator.isNumeric(normalized) && Number(normalized) <= maxNumber;
};

export const getWordCount = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
};

export const getCharacterCount = (value: string) => value.length;

export const matchesPattern = (value: string, pattern: any) => {
  const re = new RegExp(pattern);
  return re.test(value);
};

export const containsReservedCharacters = (
  value: string,
  reservedCharacters: string = defaultReservedCharacters,
) => {
  const escapedReservedCharacters = reservedCharacters.replace(/[.*+?^${}()|[\]-]/g, '\\$&');
  return !new RegExp(`^[^${escapedReservedCharacters}]*$`).test(value);
};

export const stripReservedCharacters = (
  value: string,
  reservedCharacters: string = defaultReservedCharacters,
) => {
  const escapedReservedCharacters = reservedCharacters.replace(/[.*+?^${}()|[\]-]/g, '\\$&');
  return value.replace(new RegExp(`[${escapedReservedCharacters}]`, 'g'), '');
};

export const hasValue = (value: unknown) => {
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (typeof value === 'number') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object' && value !== null) {
    return Object.keys(value).length > 0;
  }
  return false;
};

export const validateField = (
  title: string,
  rules: Rules,
  value: string,
  errorMessages?: ErrorMessages,
) => {
  const trimmedValue = typeof value === 'string' ? value.trim() : value;
  const stringValue =
    typeof trimmedValue === 'number' ? String(trimmedValue) : String(trimmedValue);

  if (rules.required && !hasValue(trimmedValue)) {
    return getErrorMessage('required', title, '', errorMessages?.required || '');
  }

  if (!rules.required && !hasValue(trimmedValue)) {
    // allow empty optional values
    return undefined;
  }

  if (
    rules.reservedCharacters &&
    containsReservedCharacters(stringValue, rules.reservedCharacters)
  ) {
    return getErrorMessage(
      'reservedCharacters',
      title,
      '',
      errorMessages?.reservedCharacters || '',
    );
  }

  if (rules.email && !validator.isEmail(stringValue)) {
    return getErrorMessage('email', title, '', errorMessages?.email || '');
  }

  if (rules.decimal) {
    const normalized = stringValue.replace(/,/g, '');
    const match = String(rules.decimal)
      .trim()
      .match(/^decimal\((\d+)\)$/i);
    const places = match ? Number.parseInt(match[1], 10) : 0;
    if (!validator.isFloat(normalized)) {
      return getErrorMessage('decimal', title, '', errorMessages?.decimal || '');
    }
    const fraction = normalized.split('.')[1] ?? '';
    if (!Number.isNaN(places) && fraction.length > places) {
      return getErrorMessage('decimal', title, '', errorMessages?.decimal || '');
    }
  }

  if (rules.float && !validator.isFloat(stringValue.replace(/,/g, ''))) {
    return getErrorMessage('float', title, '', errorMessages?.float || '');
  }

  if (rules.number && !validator.isNumeric(stringValue.replace(/,/g, ''))) {
    return getErrorMessage('number', title, '', errorMessages?.number || '');
  }

  if (rules.telephone && !validateMobile(stringValue)) {
    return getErrorMessage('telephone', title, '', errorMessages?.telephone || '');
  }

  if (rules.minLength && !isMinLength(stringValue, Number(rules.minLength))) {
    return getErrorMessage(
      'minLength',
      title,
      String(rules.minLength),
      errorMessages?.minLength || '',
    );
  }

  if (rules.maxLength && !isMaxLength(stringValue, Number(rules.maxLength))) {
    return getErrorMessage(
      'maxLength',
      title,
      String(rules.maxLength),
      errorMessages?.maxLength || '',
    );
  }

  if (rules.min && !isMinNumber(stringValue, Number(rules.min))) {
    return getErrorMessage('min', title, String(rules.min), errorMessages?.min || '');
  }

  if (rules.max && !isMaxNumber(stringValue, Number(rules.max))) {
    return getErrorMessage('max', title, String(rules.max), errorMessages?.max || '');
  }

  if (rules.pattern && !matchesPattern(trimmedValue, rules.pattern)) {
    return getErrorMessage('pattern', title, '', errorMessages?.pattern || '');
  }

  return undefined;
};

export const getInputRules = (rules: Rules, title: string, defaultMaxLength: number) => {
  const specialCharacterValidation = (value: string) =>
    !containsReservedCharacters(value, rules.reservedCharacters) ||
    getErrorMessage('reservedCharacters', title, '');

  return {
    ...rules,
    validate: { specialCharacterValidation },
    maxLength: rules.maxLength || defaultMaxLength,
  };
};

export const formDataToObject = (formData: [string, FormDataEntryValue][]) =>
  formData.reduce(
    (acc, [key, value]) => {
      acc[key] = value;
      return acc;
    },
    {} as Record<string, FormDataEntryValue>,
  );

const findInterpolatedAnswerValue = (
  answers: FieldValues | Record<string, unknown> | undefined,
  fieldName: string,
) => {
  if (!answers || typeof answers !== 'object') return undefined;

  if (fieldName in answers) {
    return answers[fieldName];
  }

  const matchedKey = Object.keys(answers).find((key) => key.endsWith(fieldName));
  return matchedKey ? answers[matchedKey] : undefined;
};

const getNestedInterpolatedAnswerValue = (
  answers: FieldValues | Record<string, unknown> | undefined,
  fieldName: string,
) => {
  const [rootFieldName, ...path] = fieldName.split('.').map((part) => part.trim());
  if (!rootFieldName || path.length === 0) return findInterpolatedAnswerValue(answers, fieldName);

  let current = findInterpolatedAnswerValue(answers, rootFieldName);
  if (typeof current === 'string') {
    try {
      current = JSON.parse(current);
    } catch (_) {
      return undefined;
    }
  }

  if (current && typeof current === 'object' && 'quarter' in current && 'year' in current) {
    const reportingPeriodHint = calculateSelectedReportingPeriodHint(current);
    current = {
      ...(current as Record<string, unknown>),
      dateRange: reportingPeriodHint.dateRange,
      quarterNumber: reportingPeriodHint.quarter,
    };
  }

  for (const key of path) {
    if (!key || !current || typeof current !== 'object' || !(key in current)) return undefined;
    current = (current as Record<string, unknown>)[key];
  }

  return current;
};

export const interpolateQuestionText = (
  content: string | undefined,
  answers?: FieldValues | Record<string, unknown>,
): string | undefined => {
  if (!content) return content;

  return content.replace(/\{([^}]+)\}/g, (_match, token) => {
    const fieldName = String(token || '').trim();
    if (!fieldName) return '';

    const answerValue = getNestedInterpolatedAnswerValue(answers, fieldName);
    if (answerValue === undefined || answerValue === null) return '';
    if (Array.isArray(answerValue)) return answerValue.join(', ');
    if (typeof answerValue === 'object') return '';

    return String(answerValue);
  });
};

const isNilReturnAnswer = (
  answers: FieldValues | Record<string, unknown> | undefined,
  override: AnswerSumNilReturnOverride | undefined,
) => {
  if (!override) return false;

  const answerValue = findInterpolatedAnswerValue(answers, override.field);
  return String(answerValue ?? '') === override.value;
};

const getAnswerSumValue = (
  answers: FieldValues | Record<string, unknown> | undefined,
  answerKey: string,
  nilReturnOverrides?: Record<string, AnswerSumNilReturnOverride>,
) => {
  if (isNilReturnAnswer(answers, nilReturnOverrides?.[answerKey])) return 0;

  const answerValue = findInterpolatedAnswerValue(answers, answerKey);
  const numericAnswer = Number(answerValue);
  return Number.isNaN(numericAnswer) ? 0 : numericAnswer;
};

export const getAnswerSumTotal = (
  answers: FieldValues | Record<string, unknown> | undefined,
  answerKeys: string[],
  nilReturnOverrides?: Record<string, AnswerSumNilReturnOverride>,
) =>
  answerKeys.reduce(
    (total, answerKey) => total + getAnswerSumValue(answers, answerKey, nilReturnOverrides),
    0,
  );

const getAnswerSumPlaceholderValues = (
  answers: FieldValues | Record<string, unknown> | undefined,
  answerSumReferences: AnswerSumReference[] | undefined,
) =>
  (answerSumReferences || []).reduce(
    (placeholderValues, reference) => ({
      ...placeholderValues,
      [reference.token]: getAnswerSumTotal(
        answers,
        reference.answerKeys,
        reference.nilReturnOverrides,
      ),
    }),
    {} as Record<string, number>,
  );

export const interpolateQuestionTextWithAnswerSums = (
  content: string | undefined,
  question: Question,
  answers?: FieldValues | Record<string, unknown>,
): string | undefined => {
  const answerSumValues = getAnswerSumPlaceholderValues(answers, question.answerSumReferences);
  return interpolateQuestionText(content, {
    ...(answers || {}),
    ...answerSumValues,
  });
};

export const getListAnswers = (formValues: FieldValues, questions: Question[]) => {
  const groupedAnswers: {
    [key: string]: { question: Question; questionName: string; answer: string; id: string }[];
  } = {};

  for (const [questionName, answer] of Object.entries(formValues)) {
    const question = questions.find((q) => questionName.endsWith(q.name));

    if (question) {
      const prefixedSuffix = `-${question.name}`;
      const id =
        questionName === question.name
          ? '1'
          : questionName.endsWith(prefixedSuffix)
            ? questionName.slice(0, -prefixedSuffix.length) || '1'
            : questionName.split('-')[0] || '1';

      if (!groupedAnswers[id]) groupedAnswers[id] = [];
      groupedAnswers[id].push({ question, questionName, answer, id });
    }
  }

  return groupedAnswers;
};

export type ListSummaryEntry = {
  id: string;
  answers: Record<string, unknown>;
};

export type ListTotalsRow = {
  key: string;
  groupValues: string[];
  totals: number[];
};

const DEFAULT_TOTALS_ROW_KEY = '__all__';

export const normalizeNumericValue = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 0;

    // Preserve scientific notation (e.g. "1E+1") while still handling currency/commas.
    const directNumeric = trimmed.replace(/[,£\s]/g, '');
    const directParsed = Number(directNumeric);
    if (Number.isFinite(directParsed)) return directParsed;

    // Fallback for mixed strings, e.g. "£10.00".
    const cleaned = trimmed.replace(/,/g, '').replace(/[^\d.-]/g, '');
    if (!cleaned) return 0;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const formatCurrencyValue = (value: string | number): string => {
  const rawValue = String(value).trim();
  const normalized = rawValue.replace(/[£,\s]/g, '');
  const scientificNotationPattern = /^-?\d+(?:\.\d+)?e[+-]?\d+$/i;

  if (scientificNotationPattern.test(normalized)) {
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) {
      const formatted = parsed.toLocaleString('en-GB', { maximumFractionDigits: 20 });
      return parsed < 0 ? `-£${formatted.slice(1)}` : `£${formatted}`;
    }
  }

  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) {
    return rawValue;
  }

  const isNegative = normalized.startsWith('-');
  const unsignedValue = isNegative ? normalized.slice(1) : normalized;
  const [whole = '', fraction] = unsignedValue.split('.', 2);
  const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formattedNumber = fraction !== undefined ? `${formattedWhole}.${fraction}` : formattedWhole;

  return isNegative ? `-£${formattedNumber}` : `£${formattedNumber}`;
};

export const formatTotalValue = (value: number, question?: Question): string => {
  if (question?.questionType === 'costInput') {
    return formatCurrencyValue(value);
  }

  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toFixed(2)));
};

export const getListSummaryEntries = (
  formValues: FieldValues,
  questions: Question[],
): ListSummaryEntry[] => {
  const groupedAnswers = getListAnswers(formValues, questions);

  const entries = Object.entries(groupedAnswers).map(([entryId, answerRows]) => {
    const answerMap = answerRows.reduce(
      (acc, current) => ({ ...acc, [current.question.name]: current.answer as unknown }),
      {} as Record<string, unknown>,
    );

    return { id: entryId, answers: answerMap };
  });

  return entries.filter((entry) => Object.values(entry.answers).some((value) => hasValue(value)));
};

export const getListTotals = (
  formValues: FieldValues,
  questions: Question[],
  totalConfig: ListTotalConfig,
): { groupBy: string[]; rows: ListTotalsRow[]; hasEntries: boolean } => {
  const groupBy = totalConfig.groupBy?.filter(Boolean) ?? [];
  const entries = getListSummaryEntries(formValues, questions);
  const equalsEntries = Object.entries(totalConfig.equals || {}).filter(
    ([field, value]) => field.trim() !== '' && value.trim() !== '',
  );
  const filteredEntries =
    equalsEntries.length > 0
      ? entries.filter((entry) =>
          equalsEntries.every(([field, expected]) => {
            const answer = entry.answers[field];
            return hasValue(answer) && String(answer) === expected;
          }),
        )
      : entries;
  const rows = new Map<string, ListTotalsRow>();

  if (entries.length === 0) {
    return { groupBy, rows: [], hasEntries: false };
  }

  if (groupBy.length === 0) {
    rows.set(DEFAULT_TOTALS_ROW_KEY, {
      key: DEFAULT_TOTALS_ROW_KEY,
      groupValues: [],
      totals: totalConfig.columns.map(() => 0),
    });
  }

  filteredEntries.forEach((entry) => {
    const groupValues = groupBy.map((fieldName) => {
      const value = entry.answers[fieldName];
      return hasValue(value) ? String(value) : 'Not provided';
    });
    const rowKey = groupBy.length > 0 ? groupValues.join('||') : DEFAULT_TOTALS_ROW_KEY;

    if (!rows.has(rowKey)) {
      rows.set(rowKey, {
        key: rowKey,
        groupValues,
        totals: totalConfig.columns.map(() => 0),
      });
    }

    const currentRow = rows.get(rowKey);
    if (!currentRow) return;

    totalConfig.columns.forEach((column, index) => {
      currentRow.totals[index] += normalizeNumericValue(entry.answers[column.field]);
    });
  });

  return { groupBy, rows: Array.from(rows.values()), hasEntries: entries.length > 0 };
};

export const filterObjectByKeys = (object: Record<string, string>, keys: string[]) => {
  return Object.keys(object)
    .filter((key) => keys.includes(key))
    .reduce(
      (acc, key) => {
        acc[key] = object[key];
        return acc;
      },
      {} as Record<string, string>,
    );
};

export const groupValidation = ({
  value,
  answers,
  question,
  sumColumns,
}: {
  value: Record<string, string>;
  answers?: FieldValues;
  question?: Question;
  sumColumns?: string[];
}): string | boolean => {
  const customValidation = question?.customValidation;
  // If sumColumns is provided, filter the value object to only include the sumColumns
  // Otherwise, use the entire value object
  const values = sumColumns ? filterObjectByKeys(value, sumColumns) : value;

  if (customValidation?.fieldsEqual) {
    for (const { fields, answer, answerValue, errorPrefix } of customValidation.fieldsEqual) {
      const { success, errorMessage } = fieldCompare({
        values,
        answers,
        question,
        fields,
        answer,
        answerValue,
        errorPrefix,
      });
      if (!success) return errorMessage || '';
    }
  }

  if (customValidation?.fieldsLessThanOrEqual) {
    for (const {
      fields,
      answer,
      answerValue,
      errorPrefix,
    } of customValidation.fieldsLessThanOrEqual) {
      const { success, errorMessage } = fieldCompare({
        values,
        answers,
        question,
        fields,
        answer,
        answerValue,
        errorPrefix,
        operator: 'lessThanOrEqual',
      });
      if (!success) return errorMessage || '';
    }
  }

  if (customValidation?.sumEqualToPreviousAnswer) {
    const { success, errorMessage } = sumEqualToPreviousAnswer({
      values,
      answers,
      question,
      previousAnswer: customValidation.sumEqualToPreviousAnswer,
    });
    if (!success) return errorMessage || '';
  }

  if (customValidation?.sumLessThanOrEqualToPreviousAnswer) {
    const { success, errorMessage } = sumLessThanOrEqualToPreviousAnswer({
      values,
      answers,
      question,
      previousAnswer: customValidation.sumLessThanOrEqualToPreviousAnswer,
    });
    if (!success) return errorMessage || '';
  }

  if (customValidation?.sumLessThanOrEqualToAnswers?.length) {
    const { success, errorMessage } = sumLessThanOrEqualToAnswers({
      values,
      answers,
      question,
      answerKeys: customValidation.sumLessThanOrEqualToAnswers,
      nilReturnOverrides: customValidation.sumLessThanOrEqualToAnswersNilReturnOverrides,
    });
    if (!success) return errorMessage || '';
  }

  return true;
};

export const checkAllFieldsHaveValues = ({
  value,
  keys,
}: {
  value: Record<string, string>;
  keys: string[];
}) => {
  const allFieldsHaveValues = keys.every((key) => value[key] && value[key] !== '');
  if (!allFieldsHaveValues) {
    return 'Please enter a value for all fields. If you have no data for this field, please input 0.';
  }
  return true;
};

export const chainValidations = (results: (string | boolean)[]): string | boolean => {
  for (const result of results) {
    if (typeof result === 'string') {
      return result;
    }
  }
  return true;
};

export const sumEqualToPreviousAnswer = ({
  values,
  answers,
  question,
  previousAnswer,
}: {
  values: Record<string, string>;
  answers?: FieldValues;
  question?: Question;
  previousAnswer: string;
}): { success: boolean; errorMessage?: string } => {
  const sum = Object.values(values).reduce(
    (acc, curr) => acc + (Number.isNaN(Number(curr)) ? 0 : Number(curr)),
    0,
  );
  const noAnswerMessage = 'Please make sure you fill in the previous question first';
  if (!answers) return { success: false, errorMessage: noAnswerMessage };
  const answer = Object.keys(answers).find((key) => key.endsWith(previousAnswer || ''));
  if (!answer) return { success: false, errorMessage: noAnswerMessage };
  const answerValue = answers[answer];
  if (sum !== Number(answerValue)) {
    return {
      success: false,
      errorMessage:
        question?.errorMessages?.sumEqualToPreviousAnswer ||
        `Totals to all questions must equal ${answerValue}. You have entered ${sum}`,
    };
  }
  return { success: true };
};

export const sumLessThanOrEqualToPreviousAnswer = ({
  values,
  answers,
  question,
  previousAnswer,
}: {
  values: Record<string, string>;
  answers?: FieldValues;
  question?: Question;
  previousAnswer: string;
}): { success: boolean; errorMessage?: string } => {
  const sum = Object.values(values).reduce(
    (acc, curr) => acc + (Number.isNaN(Number(curr)) ? 0 : Number(curr)),
    0,
  );
  const noAnswerMessage = 'Please make sure you fill in the previous question first';
  if (!answers) return { success: false, errorMessage: noAnswerMessage };
  const answer = Object.keys(answers).find((key) => key.endsWith(previousAnswer || ''));

  if (!answer) return { success: false, errorMessage: noAnswerMessage };
  const answerValue = answers[answer];
  if (sum > Number(answerValue)) {
    return {
      success: false,
      errorMessage:
        question?.errorMessages?.sumLessThanOrEqualToPreviousAnswer ||
        `Total of ${sum} must be less than or equal to ${answerValue}`,
    };
  }
  return { success: true };
};

export const sumLessThanOrEqualToAnswers = ({
  values,
  answers,
  question,
  answerKeys,
  nilReturnOverrides,
}: {
  values: Record<string, string>;
  answers?: FieldValues;
  question?: Question;
  answerKeys: string[];
  nilReturnOverrides?: Record<string, AnswerSumNilReturnOverride>;
}): { success: boolean; errorMessage?: string } => {
  const sum = Object.values(values).reduce(
    (acc, curr) => acc + (Number.isNaN(Number(curr)) ? 0 : Number(curr)),
    0,
  );
  const maxTotal = getAnswerSumTotal(answers, answerKeys, nilReturnOverrides);

  if (sum > maxTotal) {
    return {
      success: false,
      errorMessage:
        question?.errorMessages?.sumLessThanOrEqualToAnswers ||
        `Total of ${sum} must be less than or equal to ${maxTotal}`,
    };
  }

  return { success: true };
};

/**
 * FieldCompare is a custom validation function that compares the sum of the fields to the target
 *
 * @param operator - The operator to use for the comparison
 * @param values - The current question's values
 * @param answers - All previous answers
 * @param question - The current question's configuration object
 * @param fields - The array of field keys to be summed
 * @param answer - The the answer object to search
 * @param answerValue - The answer key to search for in the answer object
 * @param errorPrefix - The error prefix
 * @returns {success: boolean, errorMessage?: string} - The result of the validation
 */
export const fieldCompare = ({
  values,
  answers,
  question,
  fields,
  answer,
  answerValue,
  errorPrefix,
  operator = 'equal',
}: {
  values: Record<string, string>;
  answers?: FieldValues;
  question?: Question;
  fields: string[];
  answer: string;
  answerValue: string;
  errorPrefix?: string;
  operator?: 'equal' | 'lessThanOrEqual';
}): { success: boolean; errorMessage?: string } => {
  // Get the previously answered value
  const missingAnswerMessage = 'Please make sure you fill in the previous question first';
  const answerKey = Object.keys(answers || {}).find((key) => key.endsWith(answer || ''));
  if (!answerKey || !answers?.[answerKey])
    return { success: false, errorMessage: missingAnswerMessage };
  const answerObject = stringToObject(answers[answerKey]);
  const target = Number.isNaN(Number(answerObject[answerValue]))
    ? 0
    : Number(answerObject[answerValue]);
  if (Number.isNaN(target)) return { success: false, errorMessage: missingAnswerMessage };

  // Get the values of the fields
  const fieldValuesFiltered = filterObjectByKeys(values, fields);
  const fieldSum = Object.values(fieldValuesFiltered).reduce(
    (acc, curr) => acc + (Number.isNaN(Number(curr)) ? 0 : Number(curr)),
    0,
  );

  // Check if the fields are equal to the target
  if (operator === 'equal' && fieldSum !== target) {
    return {
      success: false,
      errorMessage:
        question?.errorMessages?.fieldsEqual ||
        `${
          errorPrefix || `The total of ${fields.join(', ')}`
        } must equal ${target}, you have entered ${fieldSum}`,
    };
  }
  // Check if the fields are less than or equal to the target
  if (operator === 'lessThanOrEqual' && fieldSum > target) {
    return {
      success: false,
      errorMessage:
        question?.errorMessages?.fieldsEqual ||
        `${errorPrefix || `The total of ${fields.join(', ')}`} must be less than or equal to ${target}, you have entered ${fieldSum}`,
    };
  }
  return { success: true };
};

// For a given object of <fieldKey, fieldValue>, return the sum of the values of the fields that match the row suffix
// If excludePattern is provided, filters out any values that match the excludePattern via regex
export const sumRow = (row: string, values: Record<string, string>, excludePattern?: string) => {
  const rowKeys = Object.keys(values).filter((key) => key.startsWith(row));
  const filteredRowKeys = excludePattern
    ? rowKeys.filter((key) => !key.match(excludePattern))
    : rowKeys;
  const rowValues = filteredRowKeys.map((key) => values[key]);
  return rowValues.reduce((acc, curr) => acc + (Number.isNaN(Number(curr)) ? 0 : Number(curr)), 0);
};

export const objectToString = (object: Record<string, string>): string => {
  try {
    return JSON.stringify(object);
  } catch (_) {
    return '';
  }
};

export const stringToObject = (string?: string): Record<string, string> => {
  try {
    return JSON.parse(string || '{}');
  } catch (_) {
    return {};
  }
};
